import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../auth/AuthContext';
import { useIsSubscribed } from '../../hooks/useIsSubscribed';
import { useTier } from '../../hooks/useTier';
import { useTranslation } from '../../hooks/useTranslation';
import chatService from '../../services/chatService';
import { trackFeatureUsed } from '../../services/analyticsService';
import './AIExpansionModal.css';
import MarkdownRenderer from '../CreditPlanFlow/MarkdownRenderer';

// Word limit for chat input to prevent abuse
const CHAT_WORD_LIMIT = 250;

const AIExpansionModal = ({ isOpen, onClose, onSidebarClose, sectionContent, fullContent, sectionTitle, pageName }) => {
  const t = useTranslation();
  const { currentUser } = useAuth();
  const { isSubscribed, loading: subscriptionLoading } = useIsSubscribed();
  const { hasFeature, loading: tierLoading } = useTier();
  const history = useHistory();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [expandedContent, setExpandedContent] = useState('');
  const [error, setError] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);
  const [chatLoading, setChatLoading] = useState(false);

  const functions = getFunctions();
  const expandSectionContent = httpsCallable(functions, 'expandSectionContent');

  // Helper function to count words in text
  const countWords = (text) => {
    if (!text || !text.trim()) return 0;
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  // Load chat history when modal opens
  useEffect(() => {
    if (isOpen && currentUser && pageName && sectionTitle) {
      loadChatHistory();
    }
  }, [isOpen, currentUser, pageName, sectionTitle]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const loadChatHistory = async () => {
    if (!currentUser || !pageName || !sectionTitle) return;
    
    try {
      setChatLoading(true);
      const historyData = await chatService.getHistory(pageName, sectionTitle);
      if (historyData.success && historyData.data.messages) {
        setChatMessages(historyData.data.messages);
      } else {
        setChatMessages([]);
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
    } finally {
      setChatLoading(false);
    }
  };

  const handleExpand = async () => {
    // Check subscription/tier before expanding
    if (subscriptionLoading || tierLoading) {
      setError(t('aiExpansion.checkingSubscription', 'Checking subscription status...'));
      return;
    }

    // Check if user's tier has AI copilot feature
    if (!hasFeature('ai_copilot')) {
      // Track subscription required event
      const section = location.pathname.startsWith('/personal-credit') ? 'personal_credit' : 
                     location.pathname.startsWith('/business-credit') ? 'business_credit' : 'dashboard';
      
      trackFeatureUsed({
        section: section,
        feature: 'ai_expand',
        action: 'blocked_no_subscription',
        source: 'modal'
      });
      
      // Close modal and sidebar before redirecting
      if (onClose) {
        onClose();
      }
      if (onSidebarClose) {
        onSidebarClose();
      }
      // Redirect to subscription management page
      history.push('/manage-subscription');
      return;
    }

    if (!sectionContent || !fullContent) {
      setError(t('aiExpansion.missingContent', 'Missing content to expand'));
      return;
    }

    // Track AI expand attempt
    const section = location.pathname.startsWith('/personal-credit') ? 'personal_credit' : 
                   location.pathname.startsWith('/business-credit') ? 'business_credit' : 'dashboard';
    
    trackFeatureUsed({
      section: section,
      feature: 'ai_expand',
      action: 'invoke',
      source: 'modal',
      additionalParams: {
        section_title: sectionTitle,
        page_name: pageName
      }
    });

    setLoading(true);
    setError('');
    setExpandedContent('');

    try {
      const response = await expandSectionContent({
        sectionContent,
        fullContent,
        sectionTitle
      });

      if (response.data.success) {
        const aiResponse = response.data.data.expandedContent;
        setExpandedContent(aiResponse);
        
        // Track successful expansion
        trackFeatureUsed({
          section: section,
          feature: 'ai_expand',
          action: 'success',
          source: 'modal',
          additionalParams: {
            section_title: sectionTitle,
            page_name: pageName
          }
        });
        
        // Save the AI response to chat history
        if (currentUser && pageName && sectionTitle) {
          try {
            await chatService.saveMessage(
              pageName, 
              sectionTitle, 
              aiResponse, 
              false, 
              aiResponse
            );
            
            // Reload chat history to include the new message
            await loadChatHistory();
          } catch (error) {
            console.error('Error saving AI response to chat history:', error);
          }
        }
      } else {
        setError(t('aiExpansion.failedToExpand', 'Failed to expand content'));
        // Track failed expansion
        trackFeatureUsed({
          section: section,
          feature: 'ai_expand',
          action: 'error',
          source: 'modal',
          additionalParams: {
            section_title: sectionTitle,
            page_name: pageName,
            error: 'api_error'
          }
        });
      }
    } catch (error) {
      console.error('Error expanding content:', error);
      setError(t('aiExpansion.failedToExpandError', 'Failed to expand content: {error}').replace('{error}', error.message));
      
      // Track error
      trackFeatureUsed({
        section: section,
        feature: 'ai_expand',
        action: 'error',
        source: 'modal',
        additionalParams: {
          section_title: sectionTitle,
          page_name: pageName,
          error: error.message
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    // Check subscription before sending message
    if (subscriptionLoading) {
      setError(t('aiExpansion.checkingSubscription', 'Checking subscription status...'));
      return;
    }

    if (!isSubscribed) {
      // Track subscription required event
      const section = location.pathname.startsWith('/personal-credit') ? 'personal_credit' : 
                     location.pathname.startsWith('/business-credit') ? 'business_credit' : 'dashboard';
      
      trackFeatureUsed({
        section: section,
        feature: 'ai_chat',
        action: 'blocked_no_subscription',
        source: 'modal'
      });
      
      // Close modal and sidebar before redirecting
      if (onClose) {
        onClose();
      }
      if (onSidebarClose) {
        onSidebarClose();
      }
      // Redirect to subscription management page
      history.push('/manage-subscription');
      return;
    }
    
    if (!newMessage.trim() || !currentUser || !pageName || !sectionTitle) return;
    
    // Check word limit
    const wordCount = countWords(newMessage);
    if (wordCount > CHAT_WORD_LIMIT) {
      setError(t('aiExpansion.wordLimitExceeded', 'Message exceeds the word limit of {limit} words. Please shorten your message.').replace('{limit}', CHAT_WORD_LIMIT.toString()));
      return;
    }
    
    const userQuestion = newMessage.trim();
    const section = location.pathname.startsWith('/personal-credit') ? 'personal_credit' : 
                   location.pathname.startsWith('/business-credit') ? 'business_credit' : 'dashboard';
    
    // Track message sent
    trackFeatureUsed({
      section: section,
      feature: 'ai_chat',
      action: 'send_message',
      source: 'modal',
      additionalParams: {
        section_title: sectionTitle,
        page_name: pageName,
        message_length: userQuestion.length
      }
    });
    
    setNewMessage('');
    setSendingMessage(true);
    
    try {
      // Add user message to local state immediately
      const userMessage = {
        id: Date.now().toString(),
        message: userQuestion,
        isUserMessage: true,
        timestamp: new Date(),
      };
      setChatMessages(prev => [...prev, userMessage]);
      
      // Send to AI and get response
      const response = await chatService.sendFollowUp(
        pageName,
        sectionTitle,
        userQuestion,
        chatMessages,
        sectionContent,
        fullContent
      );
      
      if (response.success) {
        // Track successful response
        trackFeatureUsed({
          section: section,
          feature: 'ai_chat',
          action: 'receive_response',
          source: 'modal',
          additionalParams: {
            section_title: sectionTitle,
            page_name: pageName
          }
        });
        
        // Add AI response to local state
        const aiMessage = {
          id: response.data.messageId || Date.now().toString(),
          message: response.data.aiResponse,
          isUserMessage: false,
          timestamp: new Date(),
        };
        setChatMessages(prev => [...prev, aiMessage]);
      } else {
        setError(t('aiExpansion.failedToGetResponse', 'Failed to get AI response'));
        
        // Track error
        trackFeatureUsed({
          section: section,
          feature: 'ai_chat',
          action: 'error',
          source: 'modal',
          additionalParams: {
            section_title: sectionTitle,
            page_name: pageName,
            error: 'api_error'
          }
        });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setError(t('aiExpansion.failedToSendMessage', 'Failed to send message'));
      
      // Track error
      trackFeatureUsed({
        section: section,
        feature: 'ai_chat',
        action: 'error',
        source: 'modal',
        additionalParams: {
          section_title: sectionTitle,
          page_name: pageName,
          error: error.message
        }
      });
    } finally {
      setSendingMessage(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp || typeof timestamp !== 'object' || Object.keys(timestamp).length === 0) return '';
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (!isOpen) return null;

  return (
    <div className="ai-modal-overlay" onClick={onClose}>
      <div className="ai-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ai-modal-header">
          <h3>{t('aiExpansion.title', 'AI Content Expansion & Chat')}</h3>
          <button className="ai-modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="ai-modal-content">
          {error && (
            <div className="ai-error-message">
              {error}
            </div>
          )}
          
          {/* Chat Messages Area */}
          <div className="ai-chat-messages">
            {chatLoading && (
              <div className="ai-loading">
                <div className="ai-spinner"></div>
                <p>{t('aiExpansion.loadingChat', 'Loading chat...')}</p>
              </div>
            )}
            {chatMessages.length === 0 && !expandedContent && !loading && !chatLoading && (
              <div className="ai-expand-prompt">
                <p>{t('aiExpansion.expandPrompt', 'Click the button below to get an AI-powered expansion of this section content.')}</p>
                <button 
                  className="ai-expand-button"
                  onClick={handleExpand}
                  disabled={loading}
                >
                  {t('aiExpansion.expandButton', '💎 Expand with AI')}
                </button>
              </div>
            )}
            
            {loading && (
              <div className="ai-loading">
                <div className="ai-spinner"></div>
                <p>{t('aiExpansion.generatingExpansion', 'Generating AI expansion...')}</p>
              </div>
            )}
            
            {chatMessages.map((message) => (
              <div 
                key={message.id} 
                className={`ai-message ${message.isUserMessage ? 'ai-user-message' : 'ai-ai-message'}`}
              >
                <div className="ai-message-content">
                  <div className={`ai-message-text ${message.isUserMessage ? 'ai-user-message-text' : 'ai-ai-message-text'}`}>
                    <MarkdownRenderer 
                      content={message.message}
                      showFullText={true}
                      showToggleButton={false}
                    />
                  </div>
                  <div className="ai-message-timestamp">
                    {formatTimestamp(message.timestamp)}
                  </div>
                </div>
              </div>
            ))}
            
            {sendingMessage && (
              <div className="ai-message ai-ai-message">
                <div className="ai-message-content">
                  <div className="ai-message-text">
                    <div className="ai-typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
          
          {/* Chat Input */}
          {chatMessages.length > 0 && (
            <form className="ai-chat-input-form" onSubmit={handleSendMessage}>
              <div className="ai-chat-input-container">
                <div className="ai-chat-input-wrapper">
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={t('aiExpansion.followUpPlaceholder', 'Ask a follow-up question...')}
                    className="ai-chat-input"
                    disabled={sendingMessage}
                    maxLength={2000}
                    rows={2}
                  />
                  <div className={`ai-word-count ${countWords(newMessage) > CHAT_WORD_LIMIT ? 'ai-word-count-error' : countWords(newMessage) > CHAT_WORD_LIMIT * 0.9 ? 'ai-word-count-warning' : ''}`}>
                    {t('aiExpansion.wordCount', '{count}/{limit} words').replace('{count}', countWords(newMessage).toString()).replace('{limit}', CHAT_WORD_LIMIT.toString())}
                  </div>
                </div>
                <button 
                  type="submit" 
                  className="ai-send-button"
                  disabled={!newMessage.trim() || sendingMessage || countWords(newMessage) > CHAT_WORD_LIMIT}
                >
                  {sendingMessage ? t('aiExpansion.sending', 'Sending...') : t('aiExpansion.send', 'Send')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIExpansionModal; 